import { useEffect, useState } from 'react'
import { costMicroEur } from '@fjalim/shared'
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router'
import { auth } from '../api'
import { useAuth } from '../auth/AuthContext'
import { useI18n } from '../i18n'
import { applyTheme, readTheme, type ThemeChoice } from '../lib/theme'
import { Wordmark } from './Wordmark'
import styles from './Shell.module.css'
import alFlag from '../../../../packages/shared/flags/al.svg?url'
import gbFlag from '../../../../packages/shared/flags/gb.svg?url'

const THEMES: ThemeChoice[] = ['system', 'light', 'dark']

function ThemeIcon({ theme }: { theme: ThemeChoice }) {
  return <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
    {theme === 'dark' ? <path d="M20.7 13.2A8.5 8.5 0 0 1 10.8 3.3a8.5 8.5 0 1 0 9.9 9.9Z" /> : theme === 'light' ? <><circle cx="12" cy="12" r="4" /><path d="M12 2v2m0 16v2M2 12h2m16 0h2M4.9 4.9l1.4 1.4m11.4 11.4 1.4 1.4M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" /></> : <><rect x="3" y="4" width="18" height="13" rx="2" /><path d="M8 21h8m-4-4v4" /></>}
  </svg>
}

export function Shell() {
  const { account, logout } = useAuth()
  const { t, locale, setLocale, money } = useI18n()
  const navigate = useNavigate()
  const location = useLocation()
  const [theme, setTheme] = useState<ThemeChoice>(readTheme)
  const [resent, setResent] = useState(false)
  const wide = location.pathname.startsWith('/transcriptions/')
  const lowBalance = account !== null && account.balance_micro_eur < costMicroEur(60)

  useEffect(() => applyTheme(theme), [theme])

  const cycleTheme = () => setTheme((cur) => THEMES[(THEMES.indexOf(cur) + 1) % THEMES.length]!)

  return (
    <div className={styles.shell}>
      <a className={styles.skip} href="#main">
        {t('nav.skip')}
      </a>
      <header className={`${styles.header} ${wide ? '' : styles.sticky}`}>
        <div className={`${styles.bar} ${wide ? styles.compact : ''}`}>
          <Link to="/" className={styles.brand} aria-label={t('app.name')}>
            <Wordmark />
          </Link>
          <nav className={styles.nav} aria-label={t('nav.main')}>
            <NavLink to="/" end className={({ isActive }) => (isActive ? styles.active : undefined)}>
              {t('nav.transcriptions')}
            </NavLink>
            <NavLink to="/keys" className={({ isActive }) => (isActive ? styles.active : undefined)}>
              {t('nav.keys')}
            </NavLink>
            <NavLink to="/billing" className={({ isActive }) => (isActive ? styles.active : undefined)}>
              {t('nav.billing')}
            </NavLink>
            {account?.is_superadmin ? <NavLink to="/admin" className={({ isActive }) => (isActive ? styles.active : undefined)}>{t('nav.admin')}</NavLink> : null}
          </nav>
          <div className={styles.money}>
            <Link to="/billing" className={styles.balance} title={t('balance.label')} data-low={lowBalance ? 'true' : undefined}>
              <span className="sr-only">{t('balance.label')}: </span>
              {money(account?.balance_micro_eur ?? 0)}
            </Link>
            <Link to="/billing" className={`btn btn-small ${lowBalance ? 'btn-primary' : ''}`}>
              {t('list.topup')}
            </Link>
          </div>
          <div className={styles.tools}>
            <div className={styles.langs} role="group" aria-label={t('lang.label')}>
              {(['sq', 'en'] as const).map((code) => (
                <button
                  key={code}
                  type="button"
                  className={styles.lang}
                  aria-pressed={locale === code}
                  aria-label={code === 'sq' ? 'Shqip' : 'English'}
                  title={code === 'sq' ? 'Shqip' : 'English'}
                  lang={code}
                  onClick={() => setLocale(code)}
                >
                  <img className={styles.flag} src={code === 'sq' ? alFlag : gbFlag} width="24" height="18" alt="" aria-hidden="true" />
                </button>
              ))}
            </div>
            <button type="button" className={styles.theme} onClick={cycleTheme} aria-label={`${t('theme.label')}: ${t(`theme.${theme}`)}`} title={`${t('theme.label')}: ${t(`theme.${theme}`)}`}>
              <ThemeIcon theme={theme} />
            </button>
            <button
              type="button"
              className="btn btn-quiet btn-small"
              onClick={() => void logout().then(() => navigate('/login'))}
            >
              {t('nav.logout')}
            </button>
          </div>
        </div>
      </header>
      {account && !account.email_verified ? (
        <div className={styles.banner} role="status">
          <span>{t('auth.pending.body', { email: account.email, minutes: 15 })}</span>
          <button
            type="button"
            className="btn btn-small"
            disabled={resent}
            onClick={() => void auth.resend().then(() => setResent(true))}
          >
            {resent ? t('auth.pending.sent') : t('auth.pending.resend')}
          </button>
        </div>
      ) : null}
      <main id="main" className={wide ? styles.mainWide : undefined} tabIndex={-1}>
        <Outlet />
      </main>
    </div>
  )
}
